import React, { useState } from "react";
import styled from "styled-components";
import { ChevronDown, ChevronRight, Download, RefreshCw } from "react-feather";
import { useMutation } from "react-query";
import { Button, Section, Status, Tabs } from "@cinesys/ui/components";
import Banner from "./Banner";
import NameList from "./NameList";
import { failedNote } from "./itemNote";
import { Muted } from "./ui";
import { useVaultTools } from "../useVaultTools";
import {
  cleanMessage,
  downloadText,
  errorText,
  getJobResults,
  useMyPlaceholders,
  type MyPlaceholderJob,
  type PlaceholderState,
} from "./vaultService";

type Filter = "awaiting" | "failed" | "finished";

const FILTERS: { key: Filter; label: string; empty: string }[] = [
  {
    key: "awaiting",
    label: "Awaiting",
    empty: "Nothing is waiting for an upload.",
  },
  { key: "failed", label: "Failed", empty: "No placeholders failed." },
  {
    key: "finished",
    label: "Uploaded",
    empty: "Nothing has been uploaded yet.",
  },
];

const STATES: Record<Filter, PlaceholderState[]> = {
  awaiting: ["awaiting", "creating"],
  failed: ["failed"],
  finished: ["finished"],
};

const PanelTabs = styled(Tabs)`
  margin: 0;
  padding: 0;
  width: 100%;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 15px 15px;
`;

const RowHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
`;

const JobBox = styled.div`
  span:has(> button[aria-expanded]) {
    flex: 1;
    min-width: 0;
  }

  span:has(> button[aria-expanded]) + div {
    flex: 0 0 0;
  }
`;

const DateText = styled(Muted)`
  margin-left: auto;
`;

const LIST_MAX_HEIGHT = "340px";

// Survives screen changes so the sidebar looks the same after navigating.
const panelView: { filter: Filter; openId: string | null | undefined } = {
  filter: "awaiting",
  openId: undefined,
};

const stateNote = (n: MyPlaceholderJob["items"][number]) => {
  if (n.state === "failed") {
    return failedNote(n.message);
  }
  if (n.state === "finished") {
    return <Status type="success">Uploaded</Status>;
  }
  if (n.state === "creating") {
    return <Status type="placeholder">Creating</Status>;
  }
  return n.message ? <Muted>{cleanMessage(n.message)}</Muted> : null;
};

const JobRow = ({
  job,
  filter,
  open,
  canDownload,
  onToggle,
}: {
  job: MyPlaceholderJob;
  filter: Filter;
  open: boolean;
  canDownload: boolean;
  onToggle: () => void;
}) => {
  const results = useMutation(async () => {
    const r = await getJobResults(job.jobId);
    downloadText(r.fileName, r.csv);
  });
  const items = job.items.filter((i) => STATES[filter].includes(i.state));

  return (
    <JobBox>
      <Section
        noMargin
        title={
          <RowHeader type="button" aria-expanded={open} onClick={onToggle}>
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>
              {job.source === "bulk"
                ? `Bulk CSV · ${items.length} ${
                    items.length === 1 ? "file" : "files"
                  }`
                : `Single · ${items.length} ${
                    items.length === 1 ? "part" : "parts"
                  }`}
            </span>
            <DateText>{new Date(job.createdAt).toLocaleString()}</DateText>
          </RowHeader>
        }
        footer={
          open && canDownload ? (
            <Button
              icon={<Download size={16} />}
              label="Download CSV"
              type="default"
              loading={results.isLoading}
              disabled={results.isLoading}
              onClick={() => results.mutate()}
            />
          ) : undefined
        }
      >
        {open && (
          <>
            {results.isError && <Banner error={errorText(results.error)} />}
            <NameList
              testId="placeholder-name"
              maxHeight={LIST_MAX_HEIGHT}
              items={items.map((n) => ({
                key: n.vaultId,
                text: n.vaultId,
                note: stateNote(n),
              }))}
            />
          </>
        )}
      </Section>
    </JobBox>
  );
};

const MyPlaceholdersPanel = () => {
  const mine = useMyPlaceholders();
  const vaultTools = useVaultTools();
  // The results route needs the placeholder tool; Upload-only users can still see the list.
  const canDownload =
    vaultTools.status === "ready" && vaultTools.tools.includes("placeholder");
  const [filter, setFilterState] = useState<Filter>(panelView.filter);
  const [openId, setOpenIdState] = useState<string | null | undefined>(
    panelView.openId
  );
  const setFilter = (f: Filter) => {
    panelView.filter = f;
    setFilterState(f);
  };
  const setOpenId = (id: string | null | undefined) => {
    panelView.openId = id;
    setOpenIdState(id);
  };

  const all = mine.data?.jobs ?? [];
  const jobs = all.filter((j) =>
    j.items.some((i) => STATES[filter].includes(i.state))
  );
  const open = openId === undefined ? jobs[0]?.jobId ?? null : openId;
  const counts = (f: Filter) =>
    all.reduce(
      (n, j) => n + j.items.filter((i) => STATES[f].includes(i.state)).length,
      0
    );

  return (
    <Section
      noMargin
      title="My Placeholders"
      buttons={
        <Button
          icon={<RefreshCw size={16} />}
          label="Refresh"
          type="default"
          loading={mine.isFetching}
          disabled={mine.isFetching}
          onClick={() => void mine.refetch()}
        />
      }
    >
      <PanelTabs
        tabs={FILTERS.map((f) => ({
          key: f.key,
          label: `${f.label} (${counts(f.key)})`,
        }))}
        value={filter}
        onChange={(key) => {
          setFilter(key as Filter);
          setOpenId(undefined);
        }}
      />
      <Stack>
        {mine.isError && <Banner error={errorText(mine.error)} />}
        {mine.data?.truncated && (
          <Banner warn="Only your most recent placeholders are shown." />
        )}
        {mine.data && jobs.length === 0 && (
          <Banner
            placeholder={FILTERS.find((f) => f.key === filter)?.empty}
            padTop
            padBottom
          />
        )}
        {jobs.map((j) => (
          <JobRow
            key={`${filter}-${j.jobId}`}
            job={j}
            filter={filter}
            open={open === j.jobId}
            canDownload={canDownload}
            onToggle={() => setOpenId(open === j.jobId ? null : j.jobId)}
          />
        ))}
      </Stack>
    </Section>
  );
};

export default MyPlaceholdersPanel;
